import { Alert, Stack, Typography } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';

import { ArtifactRowData, ArtifactRowVariant } from './ArtifactsTable.utils';
import { useArtifactRows } from './hooks/useArtifactRows';

const COLUMNS: Column<ArtifactRowData>[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 240,
    renderCell: TwoLineTextRenderer({
      lineOne: ({ row }) => row.name,
      lineOneProps: { variant: 'title-xs' },
      lineTwo: ({ row }) => (row.isGenerating ? 'Generating…' : undefined),
    }),
  },
  { field: 'type', headerName: 'Type', width: 120 },
  { field: 'createdBy', headerName: 'Created by', width: 160 },
  {
    field: 'updatedAt',
    headerName: 'Last modified',
    width: 160,
    renderCell: TextRenderer({
      text: ({ row }) => formatDateToMonDDYYYY(row.updatedAt),
    }),
  },
];

export function ArtifactsTable({
  householdId,
  onOpenDiagram,
  onOpenMemo,
}: {
  householdId: string;
  onOpenDiagram: (id: string) => void;
  onOpenMemo: (id: string) => void;
}) {
  const { rows, loading, error, refetch, paginatedTableProps } =
    useArtifactRows(householdId);
  // Keep the table while another page of diagrams loads, so the page
  // controls stay in place.
  const showTable = rows.length > 0 || Boolean(paginatedTableProps.rowCount);
  const onRowClick = useCallback(
    ({ row: { id, variant } }: GridRowParams<ArtifactRowData>) => {
      if (variant === ArtifactRowVariant.Diagram) {
        onOpenDiagram(id);
        return;
      }
      onOpenMemo(id);
    },
    [onOpenDiagram, onOpenMemo]
  );
  if (!loading && !error && !showTable) return null;

  return (
    <Stack spacing={2}>
      <Typography variant="title-m" component="h2">
        Recent
      </Typography>
      {error && (
        <Alert
          severity="error"
          action={
            <Button
              variant="transparent"
              size="sm"
              disabled={loading}
              onClick={refetch}
            >
              Retry
            </Button>
          }
        >
          Saved artifacts could not be loaded.
        </Alert>
      )}
      {loading && !showTable && (
        <Typography variant="body-m" role="status">
          Loading artifacts…
        </Typography>
      )}
      {showTable && (
        <DataTable
          {...paginatedTableProps}
          aria-label="Saved artifacts"
          rows={rows}
          columns={COLUMNS}
          loading={loading}
          onRowClick={onRowClick}
        />
      )}
    </Stack>
  );
}
